import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { CSSTransitionGroup } from 'react-transition-group';
import MediaQuery from 'react-responsive';

import { BounceLoader } from 'halogenium';

import { muiThemeNext } from 'helpers/MUIFiles';
import { MuiThemeProvider } from '@material-ui/core';
import { getTranslatedString } from 'helpers/i18n';

import ChargeCardDialog from './review_applicants/ChargeCardDialog';

import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import { List } from 'material-ui/List';
import RaisedButton from 'material-ui/RaisedButton';
import Select from 'components/ui/SelectField';

import { deepDiver } from 'helpers/utilities';
import { AccentColor } from 'helpers/AccentColorHelper';

import { explosionCelebration } from 'actions/notificationActions';
import {
  updateApplication,
  getApplication,
  getLease,
  openLeaseInPersonOverwriteCreator,
  chargeLeadCreditCard,
  remindApplicants,
  skipDepositPayment,
  assignUnit,
} from 'actions/landlordApplicationsActions';
import { cancelSignatureRequest } from 'actions/landlordSignatureRequestActions';
import { getSignUrlForInPerson } from 'actions/landlordSignableDocActions';
import { openEditLease, getLeaseDocumentDetails } from 'actions/LeaseDetailsActions';
import { getUnits, getUnitTypes, getProperty } from 'actions/landlordPropertiesActions';
import { getDocumentTemplatesForProperty } from 'actions/landlordApplicationsActions';
import { colors } from 'helpers/ColorHelper';
import { featureEnabled } from 'helpers/ToggleBot';
import env from 'config/constants';
import { hellosignClient } from 'helpers/hellosignClient';
import { apiGet } from 'helpers/ApiHelper';
import Auth from 'j-toker';
import debounce from 'lodash/debounce';
import styled from 'styled-components';

const APPROVAL_FLOWS = {
  pending: 0,
  pending_all_tenants: 0,
  pending_approval: 1,
  flagged: 1,
  create_lease: 2,
  pending_lease_approval: 3,
  pending_tenant_signature: 4,
  pending_landlord_signature: 5,
  pending_payment: 6,
  pending_unit_assignment: 7,
};

const BOUNCE_WAIT_IN_MS = 1000;
const debouncedHandleClick = debounce(
  (onClick, ...params) => {
    if (onClick) {
      onClick(...params);
    }
  },
  BOUNCE_WAIT_IN_MS,
  { leading: true, trailing: false }
);

const FullWidthSelect = styled(Select)`
  width: 100%;
  margin-top: 8px;
`;

const PError = styled.p`
  margin-top: 8px;
  color: red;
`;

const StyledRaisedButton = styled(RaisedButton)`
  background-color: ${(props) => props.backgroundColor || AccentColor()};
  color: ${colors.WHITE};
  border-radius: 8px;
  margin-top: 10px;
`;

const ApplicationStepper = connect(
  (store) => {
    return {
      application: store.landlordApplications.application,
      leaseDetails: store.leaseDetails.leaseDetails,
      document_id: store.applicationStatus.document_id,
      signer_id: store.applicationStatus.signer_id,
      agentEmail: store.userTypes.email,
      units: store.landlordProperties.units,
      properties: store.landlordProperties.properties,
      yardiEnabled: store.userTypes.yardiEnabled,
    };
  },
  {
    getUnits,
    updateApplication,
    getApplication,
    getLease,
    openLeaseInPersonOverwriteCreator,
    openEditLease,
    getLeaseDocumentDetails,
    explosionCelebration,
    chargeLeadCreditCard,
    remindApplicants,
    skipDepositPayment,
    cancelSignatureRequest,
    assignUnit,
    getUnitTypes,
    getDocumentTemplatesForProperty,
    getProperty,
    getSignUrlForInPerson,
  }
)(
  class ApplicationStepper extends Component {
    state = {
      showChargeCard: false,
      submittingMonthlyDeposit: false,
      fetchingLeaseDetails: false,
      skippingMonthlyDeposit: false,
      assignedUnitId: null,
      assignedPropertyId: this.props.application.property_id,
    };

    redirectToSignPage = async () => {
      const { use_hellosign, hellosign_api_app_id } = this.props.application;

      if (use_hellosign && featureEnabled('custom_lease_creator')) {
        const { sign_url } = await apiGet(
          `${Auth.getApiUrl()}/signable_doc/sign_url?application_uuid=${
            this.props.application.unique_id
          }`
        );

        hellosignClient.open(sign_url, {
          clientId: hellosign_api_app_id,
          skipDomainVerification: env.ENV !== 'production',
        });
        this.props.toggleDrawer();
        return;
      }

      if (this.props.leaseDetails.document_uuid && this.props.leaseDetails.signer_uuid) {
        browserHistory.push(
          `/companies/internal_document_portal?document_uuid=${this.props.leaseDetails.document_uuid}&document_type=lease&signer_uuid=${this.props.leaseDetails.signer_uuid}`
        );
      }
    };

    componentWillUnmount() {
      clearInterval(this.fetchingLeaseInterval);
    }

    markApplicantsApplied = async (applicationID) => {
      const formData = this.props.application;
      formData['bypass_application'] = true;
      await this.props.updateApplication(applicationID, formData);
      this.props.getApplication(applicationID);
      this.props.getApplicationsAndClearOffset();
    };

    approveApplication = async (applicationID) => {
      const formData = this.props.application;
      formData['approved'] = true;
      await this.props.updateApplication(applicationID, formData);
      this.props.getApplication(applicationID);
      this.props.getApplicationsAndClearOffset();
      this.props.explosionCelebration();
    };

    fetchLease = () => {
      this.props.getLease(this.props.application.lease_id).then(() => {
        browserHistory.push('/companies/new_lease');
      });
    };

    skipCharge = () => {
      this.setState({ skippingMonthlyDeposit: true });
      this.props
        .skipDepositPayment(this.props.application.id)
        .then((response) => {
          this.setState({ skippingMonthlyDeposit: false, showChargeCard: false });
          this.fetchLease();
        })
        .catch((response) => {
          this.setState({ skippingMonthlyDeposit: false });
        });
    };

    chargeCard = () => {
      this.setState({ submittingMonthlyDeposit: true });
      this.props
        .chargeLeadCreditCard(this.props.application.id)
        .then((response) => {
          this.setState({ submittingMonthlyDeposit: false, showChargeCard: false });
          this.fetchLease();
        })
        .catch((response) => {
          this.setState({ submittingMonthlyDeposit: false });
        });
    };

    createLease = () => {
      if (
        this.props.leaseDetails.stripe_charge_id ||
        this.props.leaseDetails.process_pad_payment ||
        this.props.leaseDetails.batch_payment_id
      ) {
        return this.setState({ showChargeCard: true });
      }

      if (featureEnabled('custom_lease_creator')) {
        this.props.getLease(this.props.application.lease_id).then(() => {
          const propertyId = this.props.application.property_id;
          this.props.getProperty(propertyId);
          this.props.getUnitTypes(propertyId);
          this.props.getUnits(propertyId, { for_multiselect: true });
          this.props.getDocumentTemplatesForProperty(propertyId);
          browserHistory.push('/companies/lease_creator');
        });
      } else {
        this.props.getLease(this.props.application.lease_id).then(() => {
          browserHistory.push('/companies/new_lease');
        });
      }
    };

    getHellosignInPersonSigning = async () => {
      const { hellosign_api_app_id, unique_id } = this.props.application;

      const { sign_url } = await this.props.getSignUrlForInPerson(unique_id);

      hellosignClient.open(sign_url, {
        clientId: hellosign_api_app_id,
        skipDomainVerification: env.ENV !== 'production',
      });

      this.props.toggleDrawer();
      return;
    };

    signInPerson = () => {
      const leaseDetails = this.props.leaseDetails;
      const { use_hellosign } = this.props.application;
      if (use_hellosign && featureEnabled('custom_lease_creator')) {
        this.getHellosignInPersonSigning();
      } else {
        this.props.openLeaseInPersonOverwriteCreator(
          leaseDetails.document_uuid,
          leaseDetails.signer_uuid,
          leaseDetails.document_type
        );
      }
    };

    assignApplicationToUnit = async () => {
      this.setState({ isLoading: true });
      await this.props.assignUnit(
        this.props.application.id,
        this.state.assignedUnitId,
        this.state.assignedPropertyId
      );
      this.props.getApplicationsAndClearOffset();
      await this.props.getApplication(this.props.application.id);
      await this.props.openEditLease(this.props.application.lease_id);
      this.setState({ isLoading: false });
    };

    render() {
      return (
        <Fragment>
          <ChargeCardDialog
            isOpen={this.state.showChargeCard}
            chargeCard={this.chargeCard}
            submittingMonthlyDeposit={this.state.submittingMonthlyDeposit}
            skippingMonthlyDeposit={this.state.skippingMonthlyDeposit}
            toggleOpen={() => this.setState({ showChargeCard: !this.state.showChargeCard })}
            skipCharge={this.skipCharge}
          />

          {this.renderApplicationDenied()}
          {this.renderApplicationCancelled()}
          {this.renderApplicationLeasedToOther()}
          {this.renderUnitReservedForOther()}
          {this.renderApplicationComplete()}
          {this.renderApplicationStepper()}
        </Fragment>
      );
    }

    renderApplicationDenied = () => {
      if (this.props.application.status !== 'application_denied') return;

      if (this.props.application.deny_application_reason)
        return <p>Reason for decline: {this.props.application.deny_application_reason}</p>;
      return <p>This application has been denied</p>;
    };

    renderApplicationCancelled = () => {
      if (this.props.application.status !== 'cancelled') return;

      return <p>This application has been cancelled.</p>;
    };

    renderApplicationLeasedToOther = () => {
      if (this.props.application.status !== 'leased_to_other_applicant') return;

      return (
        <p>
          The unit this application was pending for has been leased to another applicant. Deny this
          application, or transfer it to another unit.
        </p>
      );
    };

    renderUnitReservedForOther = () => {
      if (this.props.application.status !== 'unit_reserved_for_other') return;

      return (
        <p>
          This unit has been reserved for another applicant who has payed a deposit. Deny this
          application, or transfer it to another unit.
        </p>
      );
    };

    renderApplicationComplete = () => {
      if (this.props.application.status !== 'payment_received') return;

      return <p>This application has been completed.</p>;
    };

    renderApplicationStepper = () => {
      if (this.props.application.status === 'application_denied') return;
      if (this.props.application.status === 'cancelled') return;
      if (this.props.application.status === 'payment_received') return;
      if (this.props.application.status === 'leased_to_other_applicant') return;
      if (this.props.application.status === 'unit_reserved_for_other') return;

      const renderSteps = this.getSteps().map((label, index) => {
        return (
          <Step key={index}>
            <StepLabel>{label}</StepLabel>
            <StepContent>
              <p style={{ margin: 0 }}>{this.getStepContent(index)}</p>
              <div>{this.renderStepActions(APPROVAL_FLOWS[this.props.application.status])}</div>
            </StepContent>
          </Step>
        );
      });

      return (
        <List style={{ padding: 0 }}>
          <div className="row">
            <MuiThemeProvider theme={muiThemeNext(AccentColor())}>
              <Stepper
                activeStep={APPROVAL_FLOWS[this.props.application.status]}
                orientation="vertical"
              >
                {renderSteps}
              </Stepper>
            </MuiThemeProvider>
          </div>
        </List>
      );
    };

    renderStepActions = (step) => {
      return (
        <div style={{ marginRight: '12px 0' }}>
          {step === 0 && (
            <Fragment>
              <StyledRaisedButton
                label="Remind Applicants"
                primary
                onClick={() =>
                  debouncedHandleClick(this.props.remindApplicants, this.props.application.id)
                }
              />
              {this._renderBypassApplication()}
            </Fragment>
          )}
          {step === 1 && (
            <StyledRaisedButton
              label="Approve Application"
              primary
              onClick={() => this.approveApplication(this.props.application.id)}
              backgroundColor={
                this.props.application.can_approve_application ? AccentColor() : colors.LIGHT_GRAY
              }
              disabled={!this.props.application.can_approve_application}
            />
          )}
          {step === 2 && (
            // Disable create lease button when device is mobile and custom_lease_creator toggle is true
            // TODO: Can consolidate to a single button with fn isMobile on the disabled prop
            // using the useMediaQuery hook from react-responsive, but can't use hooks in a class component
            <Fragment>
              <MediaQuery query="(min-device-width: 751px)">
                <StyledRaisedButton
                  label="Create Lease"
                  primary
                  onClick={this.createLease}
                  disabled={
                    this.disableForYardi(this.props.application) ||
                    !this.props.application.can_create_application
                  }
                />
                {this.renderYardiMessaging(this.props.application)}
              </MediaQuery>
              <MediaQuery query="(max-device-width: 750px)">
                <StyledRaisedButton
                  label="Create Lease"
                  primary
                  onClick={this.createLease}
                  disabled={
                    this.disableForYardi(this.props.application) ||
                    !this.props.application.can_create_application
                  }
                />
                {this.renderYardiMessaging(this.props.application)}
              </MediaQuery>
            </Fragment>
          )}
          {step === 3 && (
            <StyledRaisedButton
              label="Review Lease"
              primary
              onClick={this.createLease}
              backgroundColor={
                this.props.application.can_approve_leases ? AccentColor() : colors.LIGHT_GRAY
              }
              disabled={!this.props.application.can_approve_leases}
            />
          )}
          {step === 4 && (
            <Fragment>
              <CSSTransitionGroup
                transitionName="create_lease"
                transitionEnterTimeout={500}
                transitionLeaveTimeout={500}
              >
                {this.renderSignInPerson()}
              </CSSTransitionGroup>
              <CSSTransitionGroup
                transitionName="sign_inperson"
                transitionEnterTimeout={500}
                transitionLeaveTimeout={500}
              >
                {this.renderSignInPersonLoading()}
              </CSSTransitionGroup>
            </Fragment>
          )}
          {step === 5 && (
            <React.Fragment>
              <StyledRaisedButton
                label="Sign Lease"
                primary
                onClick={this.redirectToSignPage}
                disabled={
                  this.props.application.use_hellosign &&
                  !this.props.application.signer_emails.includes(this.props.agentEmail)
                }
              />
              {this.props.application.use_hellosign &&
                !this.props.application.signer_emails.includes(this.props.agentEmail) && (
                  <p style={{ margin: '15px 0 0' }}>
                    {getTranslatedString('applications.createLease.assignedAgentOnly')}
                  </p>
                )}
            </React.Fragment>
          )}
          {step === 7 && this.renderAssignUnit()}
        </div>
      );
    };

    _renderBypassApplication = () => {
      if (!this.props.application.can_approve_application) return;

      return (
        <Fragment>
          <p style={{ margin: '15px 0 0' }}>
            If you would like to bypass the application process and generate a lease.
          </p>
          <RaisedButton
            label="Bypass Application"
            onClick={() => this.markApplicantsApplied(this.props.application.id)}
            style={{
              backgroundColor: 'white',
              color: AccentColor(),
              borderRadius: '8px',
              marginTop: '10px',
            }}
          />
        </Fragment>
      );
    };

    setLeaseCreationInterval = (leaseID) => {
      if (this.fetchingLeaseInterval) return;

      this.fetchingLeaseInterval = setInterval(() => {
        this.props.getLeaseDocumentDetails(leaseID).then((response) => {
          const documentStatus = deepDiver(response, 'value.lease_document_details.status');
          if (documentStatus && documentStatus !== 'CREATING_DOCUMENT') {
            clearInterval(this.fetchingLeaseInterval);
            this.props.openEditLease(leaseID);
          }
        });
      }, 10000);
    };

    renderSignInPersonLoading = () => {
      if (this.props.leaseDetails.document_status !== 'CREATING_DOCUMENT') return;

      this.setLeaseCreationInterval(this.props.leaseDetails.id);

      return (
        <div
          className="row"
          style={{
            marginTop: '21px',
            width: '100px',
            textAlign: 'center',
            marginLeft: '25px',
          }}
        >
          <div
            style={{
              margin: 'auto',
              marginLeft: '21px',
              marginBottom: '10px',
            }}
          >
            <BounceLoader color={AccentColor()} style={{ margin: 'auto' }} />
          </div>
          <p style={{ fontFamily: 'Circular-Bold', marginTop: '5px' }}>Creating Lease</p>
        </div>
      );
    };

    renderSignInPerson = () => {
      if (this.props.leaseDetails.document_status === 'CREATING_DOCUMENT') return;

      const customLeaseCreatorToggleForHellosign =
        featureEnabled('custom_lease_creator') && this.props.application.use_hellosign;

      const applicantsPendingSignature = this.props.application.cotenants.filter(
        (cotenant) => !cotenant.signed_lease
      );

      return (
        <Fragment>
          <p>{getTranslatedString('applications.weAreWaitingForTenantsToSign')}</p>
          {customLeaseCreatorToggleForHellosign && (
            <div style={{ marginTop: '10px' }}>
              {getTranslatedString('applications.pendingSignaturesFrom')}
              {applicantsPendingSignature.map((cotenant) => (
                <div>{`${cotenant.full_name}`}</div>
              ))}
            </div>
          )}
          <div>
            <StyledRaisedButton
              label={getTranslatedString('applications.signInPerson')}
              primary
              onClick={this.signInPerson}
              disabled={this.disableForYardi(this.props.application)}
            />
            {this.renderYardiMessaging(this.props.application)}
            {customLeaseCreatorToggleForHellosign && (
              <button
                type="button"
                className="link-button"
                style={{ marginLeft: '15px' }}
                onClick={() => this.props.cancelSignatureRequest(this.props.application.id)}
              >
                {getTranslatedString('applications.cancelSignatureRequest')}
              </button>
            )}
          </div>
        </Fragment>
      );
    };

    getAvailableUnits = (propertyId) => {
      return this.props.getUnits(propertyId, {
        for_multiselect: true,
        only_rentables: this.props.yardiEnabled,
      });
    };

    getAvailableUnitsAndAssignProperty = (propertyId) => {
      this.setState({ assignedPropertyId: propertyId, loadingUnits: true }, () =>
        this.getAvailableUnits(propertyId).then(() => {
          this.setState({ loadingUnits: false });
        })
      );
    };

    renderAssignUnit = () => {
      return (
        <Fragment>
          <FullWidthSelect
            name="property_id"
            showSearch
            placeholder="Select"
            size="large"
            value={this.state.assignedPropertyId}
            onChange={(value) => this.getAvailableUnitsAndAssignProperty(value)}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {this.props.properties.map((property) => (
              <Select.Option key={property} value={property.value}>
                {property.label}
              </Select.Option>
            ))}
          </FullWidthSelect>
          <FullWidthSelect
            name="unit_id"
            showSearch
            placeholder="Select"
            size="large"
            value={this.state.assignedUnitId}
            onChange={(value) => this.setState({ assignedUnitId: value })}
            disabled={this.props.units.length === 0 || this.state.loadingUnits}
            loading={this.state.loadingUnits}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {this.props.units.map((unit) => (
              <Select.Option key={unit} value={unit.value}>
                {unit.label}
              </Select.Option>
            ))}
          </FullWidthSelect>
          {this.props.units.length === 0 && !this.state.loadingUnits && (
            <PError>It looks like there are no available units for this property.</PError>
          )}
          <StyledRaisedButton
            label="Assign Unit"
            primary
            disabled={this.state.isLoading || !this.state.assignedUnitId}
            onClick={() => this.assignApplicationToUnit()}
            backgroundColor={this.state.isLoading ? colors.DARK_GRAY : AccentColor()}
          />
        </Fragment>
      );
    };

    disableForYardi = (applicationDetails) => {
      if (!featureEnabled('ui.applications.yardi_specific_undenial_logic')) return;
      if (applicationDetails.is_modified_lease) return;

      if (!applicationDetails.yardi_status) return;
      return (
        applicationDetails.unit_is_rentable !== true ||
        applicationDetails.yardi_status !== 'Applicant'
      );
    };

    renderYardiMessaging = (applicationDetails) => {
      if (!featureEnabled('ui.applications.yardi_specific_undenial_logic')) return;
      const unitIsRentable = applicationDetails.unit_is_rentable === true;
      const yardi_status = applicationDetails.yardi_status;
      if (!yardi_status) return;
      if (yardi_status === 'Applicant' && unitIsRentable) return;

      if (applicationDetails.is_modified_lease) {
        return (
          <div className="row">
            <i className="em em-warning" style={{ marginTop: '16px' }} />
            <p style={{ marginTop: '8px', color: colors.YUHU_RED }}>
              It looks like this is a modified lease. The completed lease document will be sent to
              Yardi, however any modified lease details will have to be manually changed in Yardi
              once the modified lease is signed.
            </p>
          </div>
        );
      }

      if (yardi_status !== 'Applicant') {
        return (
          <div className="row">
            <i className="em em-warning" style={{ marginTop: '16px' }} />
            <p style={{ marginTop: '8px', color: colors.YUHU_RED }}>
              It looks like this application has been modified on Yardi. This application is set to
              "{yardi_status}" on Yardi, but it should be "Applicant". In order to continue, set the
              status of this application to "Applicant" in Yardi.
            </p>
          </div>
        );
      }

      return (
        <div className="row">
          <i className="em em-warning" style={{ marginTop: '16px' }} />
          <p style={{ marginTop: '8px', color: colors.YUHU_RED }}>
            It looks like this unit is no longer available. To continue, ensure this unit is Rent
            Ready and Vacant or On Notice, or transfer this application to another unit.
          </p>
        </div>
      );
    };

    getSteps = () => {
      let steps = [
        'Pending',
        'Pending Approval',
        'Pending Lease Creation',
        'Pending Lease Approval',
        'Pending Tenant Signature',
        'Pending Landlord Signature',
        'Pending Deposit Payment',
      ];

      if (this.props.application.delay_unit_assignment) {
        steps.push('Pending Unit Assignment');
      }
      return steps;
    };

    getStepContent = (step) => {
      switch (step) {
        case 0:
          return 'We are waiting for all leads to apply.';
        case 1:
          return 'This application has not been approved. You can approve this application by clicking the approve button below. Once approved, you can create and send your leases.';
        case 2:
          return 'Click below to create and send a lease to your prospects.';
        case 3:
          return 'This created lease is pending approval before being sent to the applicant(s).';
        case 4:
          return '';
        case 5:
          return 'Click below to sign the lease and complete the process.';
        case 6:
          return 'Finish the leasing process by recording the deposit as paid in the payments tab.';
        case 7:
          return 'Assign a unit by selecting one below.';
        default:
          return null;
      }
    };
  }
);

export default ApplicationStepper;
